import { Abwesenheit } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abwesenheit';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { KalenderAbwesenheit } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByMitarbeiter/KalenderAbwesenheit';
import { KalenderByMitarbeiterStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByMitarbeiter/KalenderStation';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';

function toKalenderInterval(
  interval: Abwesenheit | ArbeitsbereichStation,
): KalenderAbwesenheit | KalenderByMitarbeiterStation {
  if (interval instanceof Abwesenheit) {
    return new KalenderAbwesenheit({ abwesenheit: interval });
  }

  return new KalenderByMitarbeiterStation({
    station: interval,
    arbeitsbereich: interval.arbeitsbereich,
  });
}

// Eine Station wird nicht eingezeichnet, wenn sie sich mit einer zeitlich früheren, sichtbaren Station überschneidet.
// Der Konflikt wird stattdessen nur im Popover der früheren Station angezeigt.
// Bereits ausgeblendete Stationen fließen dabei nicht mit ein, damit sich eine Verdeckung nicht
// transitiv auf spätere, ansonsten konfliktfreie Stationen überträgt.
function getVisibleStationIds(
  arbeitsbereichStationen: ArbeitsbereichStation[],
): Set<string> {
  const sortedStationen = [...arbeitsbereichStationen].sort((a, b) => {
    const vonDiff = dayjs(a.von).diff(dayjs(b.von), 'day');
    return vonDiff !== 0 ? vonDiff : a.id.localeCompare(b.id);
  });

  const visibleStationen: ArbeitsbereichStation[] = [];

  for (const station of sortedStationen) {
    const overlapsVisibleStation = visibleStationen.some(
      (visibleStation) =>
        !(
          dayjs(station.von).isAfter(visibleStation.bis, 'day') ||
          dayjs(visibleStation.von).isAfter(station.bis, 'day')
        ),
    );

    if (!overlapsVisibleStation) {
      visibleStationen.push(station);
    }
  }

  return new Set(visibleStationen.map((station) => station.id));
}

export function mapMitarbeiterKalenderIntervals({
  rotationsplan,
  startOfYear,
  endOfYear,
}: {
  rotationsplan: Rotationsplan;
  startOfYear: dayjs.Dayjs;
  endOfYear: dayjs.Dayjs;
}): {
  id: string;
  hasOverlaps: boolean;
  interval: KalenderAbwesenheit | KalenderByMitarbeiterStation;
}[] {
  const intervals: (Abwesenheit | ArbeitsbereichStation)[] = [
    ...rotationsplan.allStationen,
    ...rotationsplan.abwesenheiten,
  ]
    .filter(
      (interval) =>
        dayjs(interval.von).isSameOrBefore(endOfYear) &&
        dayjs(interval.bis).isSameOrAfter(startOfYear),
    )
    .sort((a, b) => dayjs(a.von).diff(dayjs(b.von), 'day'));

  const arbeitsbereichStationen = intervals.filter(
    (interval): interval is ArbeitsbereichStation =>
      interval instanceof ArbeitsbereichStation,
  );

  const visibleStationIds = getVisibleStationIds(arbeitsbereichStationen);

  const visibleIntervals = intervals.filter(
    (interval) =>
      !(interval instanceof ArbeitsbereichStation) ||
      visibleStationIds.has(interval.id),
  );

  return visibleIntervals.map((interval) => ({
    id: interval.id,
    hasOverlaps: intervals.some(
      (other) =>
        other.id !== interval.id &&
        ((other instanceof Abwesenheit &&
          interval instanceof ArbeitsbereichStation) ||
          (other instanceof ArbeitsbereichStation &&
            interval instanceof Abwesenheit)) &&
        dayjs(interval.von).isSameOrBefore(dayjs(other.bis)) &&
        dayjs(interval.bis).isSameOrAfter(dayjs(other.von)),
    ),
    interval: toKalenderInterval(interval),
  }));
}
